import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Moon, Sun, Search, LogOut, User as UserIcon } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useTheme } from '@/hooks/useTheme';

const LABELS: Record<string, string> = {
  '': 'Home',
  transactions: 'Transactions',
  pending: 'Pending',
  alerts: 'Alerts',
  'parse-failed': 'Parse failed',
  admin: 'Admin',
  users: 'Users',
  departments: 'Departments',
  cards: 'Cards',
  statements: 'Statements',
  new: 'New',
  edit: 'Edit',
};

function humanize(seg: string): string {
  if (LABELS[seg]) return LABELS[seg];
  // Numeric ids → '#<id>'
  if (/^\d+$/.test(seg)) return `#${seg}`;
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

export function TopNav() {
  const { pathname } = useLocation();
  const [theme, setTheme] = useTheme();
  const segments = pathname.split('/').filter(Boolean);
  const crumbs = segments.map((seg, i) => ({
    label: humanize(seg),
    href: '/' + segments.slice(0, i + 1).join('/'),
    isLast: i === segments.length - 1,
  }));

  const { data: meData } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () =>
      fetch('/api/auth/me', { credentials: 'include' }).then(
        (r) =>
          r.json() as Promise<{
            authenticated: boolean;
            user?: { id: number; email: string; name: string; role: string };
          }>,
      ),
    staleTime: 5 * 60_000,
  });

  function triggerPalette() {
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }),
    );
  }

  async function logout() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    window.location.href = '/login';
  }

  const initials = (meData?.user?.email ?? 'U?').slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background px-4 backdrop-blur supports-[backdrop-filter]:bg-background/95">
      <SidebarTrigger />
      <Separator orientation="vertical" className="h-5" />

      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {crumbs.map((c) => (
            <span key={c.href} className="flex items-center">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {c.isLast ? (
                  <BreadcrumbPage>{c.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={c.href}>{c.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </span>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="hidden h-8 gap-2 text-muted-foreground md:flex"
          onClick={triggerPalette}
        >
          <Search className="size-3.5" />
          <span className="text-xs">Tìm kiếm...</span>
          <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-full"
              aria-label="Account"
            >
              <Avatar className="size-8">
                <AvatarFallback className="text-xs font-medium">{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{meData?.user?.name ?? '—'}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {meData?.user?.email ?? '—'}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <UserIcon className="mr-2 size-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 size-4" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
